import { BusinessOutlined } from '@mui/icons-material';
import { useState } from 'react';

import {
  DashboardHeader,
  MetricsGrid,
  PendingActions,
  RecentTransactions,
  DepartmentDistribution,
} from '@/components/admin/dashboard';
import { AllPendingActionsModal } from '@/components/admin/dashboard/AllPendingActionsModal';
import { AllTransactionsModal } from '@/components/admin/dashboard/AllTransactionsModal';
import { Navbar } from '@/components/admin/general';
import { ErrorState, LoadingState } from '@/components/common';
import { adminNavItems } from '@/data/admin/general/navigationData';
import { useAdminDashboard } from '@/hooks/queries/useAnalytics';
import { useAuth } from '@/hooks/useAuth';
import {
  adaptAdminMetrics,
  adaptPendingActions,
  adaptRecentTransactions,
  adaptDepartmentDistribution,
} from '@/utils/dashboardAdapters';

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { data: dashboardData, isLoading, error, refetch } = useAdminDashboard();
  const [showTransactionsModal, setShowTransactionsModal] = useState(false);
  const [showPendingActionsModal, setShowPendingActionsModal] = useState(false);

  if (isLoading) {
    return (
      <div className="min-h-screen w-full">
        <Navbar
          navItems={adminNavItems}
          activeItem="Dashboard General"
          userName={user?.name || 'Admin'}
          pageTitle="Dashboard Empresarial"
          
        />
        <main>
          <LoadingState message="Cargando dashboard..." />
        </main>
      </div>
    );
  }

  if (error || !dashboardData) {
    return (
      <div className="min-h-screen w-full">
        <Navbar
          navItems={adminNavItems}
          activeItem="Dashboard General"
          userName={user?.name || 'Admin'}
          pageTitle="Dashboard Empresarial"
          
        />
        <main>
          <ErrorState
            title="Error al cargar el dashboard"
            message="No se pudo cargar la información del dashboard. Por favor, intenta nuevamente."
            error={error}
            showRetry
            onRetry={() => void refetch()}
          />
        </main>
      </div>
    );
  }

  // Adapt data for components
  const adaptedMetrics = adaptAdminMetrics(dashboardData.metrics);
  const adaptedPendingActions = adaptPendingActions(dashboardData.pendingActions.actions);
  const adaptedTransactions = adaptRecentTransactions(
    dashboardData.recentTransactions.transactions
  );
  const adaptedDepartments = adaptDepartmentDistribution(dashboardData.departmentDistribution);

  return (
    <div className="min-h-screen w-full">
      {/* Navbar */}
      <Navbar
        navItems={adminNavItems}
        activeItem="Dashboard General"
        userName={user?.name || 'Admin'}
        pageTitle="Dashboard Empresarial"
        
      />

      <main className="px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <BusinessOutlined className="text-brand-blue" />
            <h1 className="text-2xl font-bold text-brand-white">Dashboard Empresarial</h1>
          </div>
          <p className="text-muted">
            Vista general de {dashboardData.overview.organizationName} •{' '}
            {dashboardData.overview.activeEmployeesCount} empleados activos
          </p>
        </div>

        {/* Hero Section */}
        <DashboardHeader
          
          title="Panel Administrativo Finara"
          subtitle="Plataforma integral para gestionar pagos empresariales, operaciones de nómina y administración de empleados con la máxima seguridad y eficiencia."
          showRealizarPago={true}
          activeEmployees={dashboardData.overview.activeEmployeesCount}
          averageProcessingTime={dashboardData.overview.averageProcessingTime}
        />

        {/* Metrics Grid */}
        <div className="mb-8">
          <MetricsGrid metrics={adaptedMetrics} />
        </div>

        {/* Pending Actions */}
        <div className="mb-8">
          <PendingActions
            actions={adaptedPendingActions}
            onViewAll={() => setShowPendingActionsModal(true)}
          />
        </div>

        {/* Main Content Grid */}
        <div className="lg:col-span-1 mb-8">
          <RecentTransactions
            transactions={adaptedTransactions}
            onViewAll={() => setShowTransactionsModal(true)}
          />
        </div>

        {/* Bottom Section - Department Distribution */}
        {adaptedDepartments.length > 0 && (
          <div className="pb-8">
            <DepartmentDistribution departments={adaptedDepartments} />
          </div>
        )}

        {/* Modals */}
        <AllTransactionsModal
          isOpen={showTransactionsModal}
          onClose={() => setShowTransactionsModal(false)}
        />
        <AllPendingActionsModal
          isOpen={showPendingActionsModal}
          onClose={() => setShowPendingActionsModal(false)}
        />
      </main>
    </div>
  );
};
